/* global WheelEvent, PointerEvent, MouseEvent, EventListener, AddEventListenerOptions */
import { onBeforeUnmount, onMounted, ref, type Ref } from 'vue';

/**
 * Pointer navigation for a horizontal, scroll-snapped rail (cas-9b1fd).
 *
 * Touch swipes and keyboard arrows scroll a rail natively. A mouse does not: a
 * vertical wheel scrolls the page (or the sheet the rail sits in), and a drag
 * picks up the cover image instead of moving the row. In prod that left the
 * Make It Better "Videos from @handle" picker showing ~4 of its videos.
 *
 * - wheel: a mostly vertical wheel over the rail scrolls it sideways while the
 *   rail can still move that way, then hands the wheel back to the page;
 *   sideways (trackpad) and shift+wheel stay native;
 * - drag: a mouse drag past a small slop scrolls the rail and swallows the
 *   click it would end in, so it never picks the tile it started on;
 * - page(±1): the prev/next buttons, one rail-width of whole tiles at a time.
 *
 * Free scrolling (wheel, drag) turns snapping off and then settles on the
 * next tile in the direction it moved, so snap never fights a sub-tile scroll
 * and a single slow wheel notch still advances a tile. Tile starts are the
 * children's `offsetLeft`, so the rail must be their offset parent.
 */

const EDGE = 2;
const DRAG_SLOP = 6;
const SETTLE_MS = 140;
const SNAP_RESTORE_MS = 450;
const LINE_PX = 16;

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

export function useRailPointerNav(railEl: Ref<HTMLElement | null>, enabled: () => boolean) {
  const canPrev = ref(false);
  const canNext = ref(false);

  let settleTimer: ReturnType<typeof setTimeout> | null = null;
  let restoreTimer: ReturnType<typeof setTimeout> | null = null;
  let drag: { id: number; startX: number; startLeft: number; moved: boolean } | null = null;
  let swallowClick = false;
  let lastDirection: 1 | -1 = 1;
  let resize: InstanceType<typeof globalThis.ResizeObserver> | null = null;
  let mutation: InstanceType<typeof globalThis.MutationObserver> | null = null;

  const maxLeft = (rail: HTMLElement) => Math.max(0, rail.scrollWidth - rail.clientWidth);
  const tiles = (rail: HTMLElement) => Array.from(rail.children) as HTMLElement[];

  function measure(): void {
    const rail = railEl.value;
    if (!rail) return;
    canPrev.value = rail.scrollLeft > EDGE;
    canNext.value = rail.scrollLeft < maxLeft(rail) - EDGE;
  }

  function clearTimers(): void {
    if (settleTimer) clearTimeout(settleTimer);
    if (restoreTimer) clearTimeout(restoreTimer);
    settleTimer = null;
    restoreTimer = null;
  }

  function freeScroll(rail: HTMLElement): void {
    clearTimers();
    rail.style.scrollSnapType = 'none';
  }

  /**
   * Glide to the next tile start in the direction the rail last moved, then
   * let snapping take over again. Directional, not nearest: one wheel notch
   * is far less than half a tile, so "nearest" would snap it straight back.
   */
  function settle(): void {
    const rail = railEl.value;
    if (!rail) return;
    const left = rail.scrollLeft;
    const max = maxLeft(rail);
    const starts = tiles(rail)
      .map(tile => clamp(tile.offsetLeft, 0, max))
      .sort((a, b) => a - b);
    const target =
      lastDirection > 0
        ? (starts.find(start => start >= left - EDGE) ?? max)
        : ([...starts].reverse().find(start => start <= left + EDGE) ?? 0);
    rail.scrollTo({ left: target, behavior: 'smooth' });
    restoreTimer = setTimeout(() => {
      restoreTimer = null;
      rail.style.scrollSnapType = '';
    }, SNAP_RESTORE_MS);
  }

  function scheduleSettle(): void {
    if (settleTimer) clearTimeout(settleTimer);
    settleTimer = setTimeout(() => {
      settleTimer = null;
      settle();
    }, SETTLE_MS);
  }

  function page(direction: 1 | -1): void {
    const rail = railEl.value;
    if (!rail) return;
    const cards = tiles(rail);
    if (cards.length === 0) return;
    const left = rail.scrollLeft;
    const view = rail.clientWidth;
    const starts = cards.map(card => card.offsetLeft);
    let first = starts.findIndex(start => start >= left - EDGE);
    if (first < 0) first = cards.length - 1;
    const whole = cards.filter((card, i) => {
      const start = starts[i] ?? 0;
      return start >= left - EDGE && start + card.offsetWidth <= left + view + EDGE;
    }).length;
    const index = clamp(first + direction * Math.max(1, whole), 0, cards.length - 1);
    const target = clamp(starts[index] ?? 0, 0, maxLeft(rail));
    rail.scrollTo({ left: target, behavior: 'smooth' });
  }

  function onWheel(event: WheelEvent): void {
    const rail = railEl.value;
    if (!rail || event.ctrlKey || event.shiftKey) return;
    if (Math.abs(event.deltaX) >= Math.abs(event.deltaY)) return;
    const unit = event.deltaMode === 1 ? LINE_PX : event.deltaMode === 2 ? rail.clientWidth : 1;
    const delta = event.deltaY * unit;
    const atEnd = delta < 0 ? rail.scrollLeft <= EDGE : rail.scrollLeft >= maxLeft(rail) - EDGE;
    if (atEnd) return;
    event.preventDefault();
    freeScroll(rail);
    lastDirection = delta > 0 ? 1 : -1;
    rail.scrollLeft += delta;
    measure();
    scheduleSettle();
  }

  function onPointerDown(event: PointerEvent): void {
    const rail = railEl.value;
    swallowClick = false;
    if (!rail || event.pointerType !== 'mouse' || event.button !== 0) return;
    drag = { id: event.pointerId, startX: event.clientX, startLeft: rail.scrollLeft, moved: false };
  }

  function onPointerMove(event: PointerEvent): void {
    const rail = railEl.value;
    if (!rail || !drag) return;
    const dx = event.clientX - drag.startX;
    if (!drag.moved) {
      if (Math.abs(dx) < DRAG_SLOP) return;
      drag.moved = true;
      freeScroll(rail);
      rail.classList.add('card-rail--dragging');
      try {
        rail.setPointerCapture?.(drag.id);
      } catch {
        // The pointer may already be gone; the drag still works uncaptured.
      }
    }
    const next = drag.startLeft - dx;
    if (next !== rail.scrollLeft) lastDirection = next > rail.scrollLeft ? 1 : -1;
    rail.scrollLeft = next;
    measure();
  }

  function endDrag(event: PointerEvent): void {
    const rail = railEl.value;
    if (!rail || !drag) return;
    const { id, moved } = drag;
    drag = null;
    if (!moved) return;
    rail.classList.remove('card-rail--dragging');
    try {
      if (rail.hasPointerCapture?.(id)) rail.releasePointerCapture(id);
    } catch {
      // Already released.
    }
    swallowClick = event.type === 'pointerup';
    settle();
  }

  function onClickCapture(event: MouseEvent): void {
    if (!swallowClick) return;
    swallowClick = false;
    event.preventDefault();
    event.stopPropagation();
  }

  function onDragStart(event: Event): void {
    event.preventDefault();
  }

  const listeners: Array<[string, EventListener, AddEventListenerOptions | boolean]> = [
    ['scroll', measure as EventListener, { passive: true }],
    ['wheel', onWheel as EventListener, { passive: false }],
    ['pointerdown', onPointerDown as EventListener, false],
    ['pointermove', onPointerMove as EventListener, false],
    ['pointerup', endDrag as EventListener, false],
    ['pointercancel', endDrag as EventListener, false],
    ['click', onClickCapture as EventListener, true],
    ['dragstart', onDragStart, false]
  ];
  let bound: HTMLElement | null = null;

  onMounted(() => {
    const rail = railEl.value;
    if (!enabled() || !rail) return;
    bound = rail;
    for (const [type, fn, opts] of listeners) rail.addEventListener(type, fn, opts);
    if (typeof window.ResizeObserver !== 'undefined') {
      resize = new window.ResizeObserver(measure);
      resize.observe(rail);
    }
    if (typeof window.MutationObserver !== 'undefined') {
      mutation = new window.MutationObserver(measure);
      mutation.observe(rail, { childList: true });
    }
    measure();
  });

  onBeforeUnmount(() => {
    clearTimers();
    resize?.disconnect();
    mutation?.disconnect();
    if (bound) for (const [type, fn, opts] of listeners) bound.removeEventListener(type, fn, opts);
    bound = null;
  });

  return { canPrev, canNext, page };
}
